/**
 * Formatting helpers.
 *
 * All of these are pure and take a value plus, where a clock is involved, an
 * explicit `now`. Nothing here calls `Date.now()` internally, which is what
 * makes the output testable and what stops a component from rendering a
 * different string on every re-render.
 */

const RELATIVE = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
const ABSOLUTE = new Intl.DateTimeFormat('en', {
  dateStyle: 'medium',
  timeStyle: 'short',
});
const ABSOLUTE_WITH_SECONDS = new Intl.DateTimeFormat('en', {
  dateStyle: 'medium',
  timeStyle: 'medium',
});

const UNITS: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
  ['year', 31_536_000],
  ['month', 2_592_000],
  ['week', 604_800],
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
];

/**
 * "3 minutes ago", "in 2 days".
 *
 * Relative time is the right rendering for anything within a month, because the
 * question a reader is asking is "is this recent?" — and `14:32:07.221Z` does not
 * answer it. Beyond a month the relative form ("2 months ago") stops being
 * informative and the absolute date takes over.
 *
 * A future timestamp is rendered relatively too, which is what makes the same
 * function usable for "expires in 6 days" and "created 2 hours ago".
 */
export function relativeTime(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return 'never';
  const timestamp = Date.parse(iso);
  if (Number.isNaN(timestamp)) return 'unknown';

  const deltaSeconds = (timestamp - now) / 1000;
  const absolute = Math.abs(deltaSeconds);

  if (absolute < 45) return 'just now';
  if (absolute > 2_592_000) return absoluteDate(iso);

  for (const [unit, seconds] of UNITS) {
    if (absolute >= seconds) {
      return RELATIVE.format(Math.round(deltaSeconds / seconds), unit);
    }
  }
  return RELATIVE.format(Math.round(deltaSeconds / 60), 'minute');
}

/** An absolute date and time, in the reader's locale and timezone. */
export function absoluteDate(iso: string | null | undefined, withSeconds = false): string {
  if (!iso) return '—';
  const timestamp = Date.parse(iso);
  if (Number.isNaN(timestamp)) return '—';
  return (withSeconds ? ABSOLUTE_WITH_SECONDS : ABSOLUTE).format(timestamp);
}

/**
 * A `<time>` element's `dateTime` attribute, or undefined for a bad value.
 *
 * `dateTime` must be a machine-readable value; feeding it "never" produces
 * invalid HTML that assistive technology reads as a literal string.
 */
export function dateTimeAttr(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  return Number.isNaN(Date.parse(iso)) ? undefined : iso;
}

/**
 * How long until something expires, in the units a person would use.
 *
 * Returns `null` for a timestamp in the past so the caller can decide between
 * "expired" and hiding the field entirely — the two are different messages and
 * this function should not pick one.
 */
export function untilExpiry(iso: string | null | undefined, now: number = Date.now()): string | null {
  if (!iso) return null;
  const timestamp = Date.parse(iso);
  if (Number.isNaN(timestamp)) return null;

  const seconds = Math.round((timestamp - now) / 1000);
  if (seconds <= 0) return null;

  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))} min`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86_400)} d`;
}

/** Initials for an avatar placeholder, from a display name or an email. */
export function initials(name: string): string {
  const cleaned = name.trim();
  if (cleaned.length === 0) return '?';

  const words = cleaned.split(/\s+/).filter((word) => word.length > 0);
  const first = words[0];
  if (!first) return '?';

  if (words.length === 1) {
    // A single word is usually an email local-part or a handle. Two characters
    // read better than one, and `@` and `.` are not initials.
    const letters = first.replace(/[^A-Za-z0-9]/g, '');
    return (letters.slice(0, 2) || first.slice(0, 1)).toUpperCase();
  }

  const last = words[words.length - 1] ?? first;
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

/**
 * A deterministic background for an avatar, derived from the identity.
 *
 * The same user gets the same colour on every page and every reload, which is
 * what makes an avatar useful for scanning a member list. A random colour would
 * be worse than no colour.
 */
export function avatarTint(seed: string): string {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }
  const hues = [265, 215, 175, 145, 35, 15, 340, 300];
  const hue = hues[hash % hues.length] ?? 265;
  return `hsl(${hue} 55% 45%)`;
}

/** "1 member", "3 members". */
export function pluralise(count: number, singular: string, plural?: string): string {
  return `${count} ${count === 1 ? singular : (plural ?? `${singular}s`)}`;
}

/** A compact byte count: "512 B", "48.2 KB", "2.0 MB". */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * A user agent string reduced to something a person can act on.
 *
 * The full header is 120 characters of `Mozilla/5.0 (Macintosh; Intel Mac OS X
 * 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/131...`, of which the
 * only useful part is "Chrome on macOS". The raw value is still available in the
 * audit log for anyone who needs it; the sessions list is a place where a reader
 * is looking for the odd one out.
 */
export function describeUserAgent(userAgent: string): { label: string; detail: string } {
  const raw = userAgent.trim();
  if (raw.length === 0) return { label: 'Unknown client', detail: '' };

  const browser =
    /\bEdg\//.test(raw) ? 'Edge'
    : /\bOPR\/|\bOpera\b/.test(raw) ? 'Opera'
    : /\bFirefox\//.test(raw) ? 'Firefox'
    : /\bChrome\//.test(raw) ? 'Chrome'
    : /\bSafari\//.test(raw) ? 'Safari'
    : /\bcurl\//.test(raw) ? 'curl'
    : /\bnode\b|\bnode-fetch\b/i.test(raw) ? 'Node'
    : /\bPostmanRuntime\//.test(raw) ? 'Postman'
    : 'Unknown browser';

  const platform =
    /\bWindows NT\b/.test(raw) ? 'Windows'
    : /\biPhone\b|\biPad\b/.test(raw) ? 'iOS'
    : /\bMac OS X\b|\bMacintosh\b/.test(raw) ? 'macOS'
    : /\bAndroid\b/.test(raw) ? 'Android'
    : /\bLinux\b/.test(raw) ? 'Linux'
    : '';

  return {
    label: platform ? `${browser} on ${platform}` : browser,
    detail: raw,
  };
}

/**
 * A human label for an audit action.
 *
 * The stored action is `organization.member.role_changed` — stable, greppable
 * and never reworded. What a reader sees is "Member role changed". Keeping the
 * two separate means the audit log can be re-worded without invalidating every
 * stored row, and a filter can match on the raw value.
 */
export function humaniseAction(action: string): string {
  const leaf = action.includes('.') ? (action.split('.').pop() ?? action) : action;
  const words = leaf.replace(/[_-]+/g, ' ').trim();
  if (words.length === 0) return action;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The first segment of an audit action, used to group and colour it. */
export function actionGroup(action: string): string {
  return action.split('.')[0] ?? action;
}

/** A role name with its first letter capitalised, for display. */
export function titleCase(value: string): string {
  if (value.length === 0) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** Truncates a long identifier in the middle, keeping both ends recognisable. */
export function shortId(value: string, keep = 8): string {
  if (value.length <= keep * 2 + 1) return value;
  return `${value.slice(0, keep)}…${value.slice(-4)}`;
}
